import { useQuery } from '@apollo/client';
import { BarChart, Folder, MoreVert, type SvgIconComponent } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeletePlaylistFolder, useUpdatePlaylistFolder } from '~/api/playlist-folders';
import { useDeletePlaylist, useMovePlaylist } from '~/api/playlists';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { MoveFolderDialog } from '~/components/dialogs/MoveFolderDialog';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  tableActionButtonProps,
  type GridActionCellProps,
} from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import { PlaylistMenuPlaylistFolder, PlaylistRenameFolderDialog } from '.';
import type {
  PlaylistList__Playlist as Playlist,
  PlaylistList__PlaylistFolder,
} from '../../queries/queries.generated';
import { useSelectedTagParams } from '../lib/filterHooks';
import { PlaylistMoveDocument } from './PlaylistMove.generated';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  item: Playlist | PlaylistList__PlaylistFolder;
  Icon?: SvgIconComponent;
}

export const Menu = ({ buttonType = 'icon', item, Icon = MoreVert, ...cellProps }: MenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const tags = useSelectedTagParams();
  const navigate = useNavigate();
  const link = useLink();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<typeof item>();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deletePlaylist] = useDeletePlaylist();
  const [deleteFolder] = useDeletePlaylistFolder();
  const [movePlaylist, movePlaylistInfo] = useMovePlaylist();
  const [updatePlaylistFolder, updatePlaylistFolderInfo] = useUpdatePlaylistFolder();
  const [movePlaylistDialogOpen, setMovePlaylistDialogOpen] = useState(false);
  const [moveFolderDialogOpen, setMoveFolderDialogOpen] = useState(false);
  const [renameFolderDialogOpen, setRenameFolderDialogOpen] = useState(false);

  const { data: playlistMoveData } = useQuery(PlaylistMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !movePlaylistDialogOpen && !moveFolderDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      playlistMoveData?.network?.playlistFolders
        .filter((x) => x.canUpdate.value)
        .map((x) => x.id) ?? [],
    [playlistMoveData],
  );

  const onMovePlaylist = () => {
    assert(moreMenuProps.context != null, 'media: no context');
    setMovePlaylistDialogOpen(true);
    moreMenuProps.onClose();
  };

  const itemActionProps = mergeGridActionProps(moreTableActionProps(item), cellProps);

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('pages.playlists.menu.actions_tooltip')}
          {...tableActionButtonProps(itemActionProps)}
          size="small"
          color="secondary"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...itemActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        {item.__typename === 'Playlist' && (
          <MenuItem onClick={onMovePlaylist}>
            <ListItemIcon>
              <Folder />
            </ListItemIcon>
            <ListItemText>{t('pages.playlists.menu.move_to_folder')}</ListItemText>
          </MenuItem>
        )}
        {item.__typename === 'Playlist' && (
          <MenuItem
            onClick={() => {
              navigate(link(`/playlists/${item.playlistFolderId}/playlist/${item.id}/usage`));
              moreMenuProps.onClose();
            }}
          >
            <ListItemIcon>
              <BarChart />
            </ListItemIcon>
            <ListItemText>{t('pages.playlists.menu.usage')}</ListItemText>
          </MenuItem>
        )}
        {item.__typename === 'PlaylistFolder' && (
          <PlaylistMenuPlaylistFolder
            item={item}
            onMove={() => {
              assert(moreMenuProps.context != null, 'media: no context');
              setMoveFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
            onRename={() => {
              setRenameFolderDialogOpen(true);
              moreMenuProps.onClose();
            }}
          />
        )}

        <Divider />

        <MoreDeleteAction
          {...moreActionProps}
          tooltip={item.canDestroy.reasons?.fullMessages.join(', ')}
          disabled={!item.canDestroy.value}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            if (item.__typename === 'PlaylistFolder') {
              await deleteFolder({
                variables: {
                  condition: { tags },
                  input: { playlistFolderId: moreMenuProps.context.id },
                },
              });
            } else {
              await deletePlaylist({
                refetchQueries: ['PlaylistList'],
                variables: { playlistIds: [moreMenuProps.context.id] },
              });
            }
            moreMenuProps.onClose();
          }}
        />
      </MuiMenu>

      <MoveToFolderDialog
        editableFolderIds={editableFolderIds}
        onSubmit={async (targetFolderId) => {
          await movePlaylist({
            refetchQueries: ['PlaylistList'],
            variables: {
              input: {
                playlistFolderId: targetFolderId,
                playlistIds: [item.id],
              },
            },
          });
        }}
        loading={movePlaylistInfo.loading}
        onClose={() => setMovePlaylistDialogOpen(false)}
        open={movePlaylistDialogOpen}
        i18nKey="pages.playlists.dialogs.move_playlist.title"
      />
      <MoveFolderDialog
        editableFolderIds={editableFolderIds}
        folderId={item.id}
        onSubmit={async (targetFolderId) => {
          await updatePlaylistFolder({
            refetchQueries: ['PlaylistList'],
            variables: {
              input: {
                playlistFolderId: item.id,
                patch: { parentId: targetFolderId },
              },
            },
          });
        }}
        loading={updatePlaylistFolderInfo.loading}
        onClose={() => setMoveFolderDialogOpen(false)}
        open={moveFolderDialogOpen}
        i18nKey="common.dialogs.move_folder.title"
      />
      {item.__typename === 'PlaylistFolder' && (
        <PlaylistRenameFolderDialog
          playlistFolder={item}
          onClose={() => setRenameFolderDialogOpen(false)}
          open={renameFolderDialogOpen}
        />
      )}
      <ConfirmDialog
        {...confirmDeleteProps}
        title={
          item.__typename === 'PlaylistFolder'
            ? t('pages.playlists.dialogs.delete_folder.title')
            : t('pages.playlists.dialogs.delete_playlist.title')
        }
        prompt={
          item.__typename === 'PlaylistFolder'
            ? t('pages.playlists.dialogs.delete_folder.prompt')
            : t('pages.playlists.dialogs.delete_playlist.prompt')
        }
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
      />
    </>
  );
};
